import { lazy, type ComponentType, type LazyExoticComponent } from "react"

export type PreloadableComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  /** Start loading the component's chunk now (idempotent; a failed load can be retried). */
  preload: () => Promise<unknown>
}

/**
 * React.lazy whose chunk can be fetched before the component renders — on
 * menu hover, while the app is idle — so opening the page doesn't wait for
 * its code. Render and preload share one import promise.
 */
export function lazyWithPreload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>): PreloadableComponent<T> {
  let loading: Promise<{ default: T }> | null = null
  const load = () => {
    loading ??= factory().catch((error) => {
      loading = null
      throw error
    })
    return loading
  }
  const component = lazy(load) as PreloadableComponent<T>
  component.preload = load
  return component
}

export const isPreloadable = (component: unknown): component is { preload: () => Promise<unknown> } =>
  (typeof component === "object" || typeof component === "function") &&
  component !== null &&
  typeof (component as { preload?: unknown }).preload === "function"
